import { useState, FC, FormEvent, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { styled, Flex, Container, Stack } from "../styled-system/jsx";
import { LuX } from "react-icons/lu";


import { Data, getLivemapsFromDB, initializeDB } from './localDB';
import {
    listMapWithProperty,
    valueOfProperty,
    getAuthorFromProperty,
    getValueWithMapId,
    getNestedValue
} from './searchUtils';
import { Button } from '../src/components/Button';
import { Input } from '../src/components/Input';
import { Select } from '../src/components/Select';

import './../src/index.css';

// Styled components
const StyledContainer = styled(Container, {
    base: {
        maxW: 'xl',
        margin: '0 auto',
        padding: '40px 24px',
        background: 'white',
        borderRadius: '2xl',
        boxShadow: '0 4px 32px 0 rgba(0,0,0,0.08)',
        minHeight: '90vh',
    }
});

const Heading = styled('h1', {
    base: {
        fontSize: '2rem',
        fontWeight: 'bold',
        margin: '0 0 20px 0',
        color: 'main.text.1',
        letterSpacing: '-0.5px',
    }
});

const SubHeading = styled('h3', {
    base: {
        fontSize: '1.15rem',
        fontWeight: 'semibold',
        margin: '20px 0 10px 0',
        color: 'gray.700',
    }
});

const Label = styled('label', {
    base: {
        fontWeight: 'medium',
        marginBottom: '6px',
        display: 'block',
        color: 'gray.700',
        fontSize: '1rem',
    }
});

const ModalOverlay = styled('div', {
    base: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(30,41,59,0.18)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(2px)',
    }
});

const ModalContent = styled('div', {
    base: {
        background: 'white',
        borderRadius: '2xl',
        boxShadow: '0 8px 32px 0 rgba(30,41,59,0.18)',
        padding: '32px 24px',
        minWidth: '340px',
        maxWidth: '95vw',
        animation: 'fadeIn 0.2s',
    }
});

const ModalHeader = styled('div', {
    base: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '18px',
        fontWeight: 'bold',
        fontSize: '1.1rem',
        color: 'blue.700',
    }
});

const ModalFooter = styled('div', {
    base: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '18px',
    }
});

const Row = styled('div', {
    base: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        marginBottom: '10px',
    }
});

const Col = styled('div', {
    base: {
        flex: '1 1 0',
        minWidth: '140px',
        alignSelf: 'center',
    }
});

const Divider = styled('hr', {
    base: {
        margin: '20px 0',
        border: 'none',
        borderTop: '1.5px solid #e2e8f0',
    }
});

const Pre = styled('pre', {
    base: {
        background: 'linear-gradient(90deg, #f1f5f9 0%, #e0e7ef 100%)',
        borderRadius: 'lg',
        padding: '18px',
        overflowX: 'auto',
        fontSize: '1rem',
        color: 'gray.800',
        marginTop: '8px',
        boxShadow: '0 2px 8px 0 rgba(30,41,59,0.04)',
    }
});

const STORAGE_KEY = 'savedSearches';

const saveSearch = (search: SavedSearch) => {
    const savedSearches = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    savedSearches.push(search);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(savedSearches));
};

const getSavedSearches = (): SavedSearch[] => {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
};

const deleteSavedSearch = (index: number) => {
    const savedSearches = getSavedSearches();
    savedSearches.splice(index, 1);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(savedSearches));
};

type Comparison = 'contains' | 'equals' | 'notContains' | 'isDefined';

type Filter = {
    type: 'author.name';
    comparison: Comparison;
    value: string;
} | {
    type: 'snippet.property';
    label: string;
    comparison: Comparison;
    value: string;
}

const FILTER_TYPE = [
    { value: 'snippet.property', label: 'Propriété du snippet' },
    { value: 'author.name', label: 'Auteur' }
];

const COMPARISON_OPTIONS = [
    { value: 'contains', label: 'Contient' },
    { value: 'notContains', label: 'Ne contient pas' },
    { value: 'equals', label: 'Est égal à' },
    { value: 'isDefined', label: 'Est utilisé' }
];

const filterMaps = (maps: Data[], filters: Array<Filter>) => {
    if (!filters.length) {
        return maps;
    }

    return maps.filter((map) => {
        let valid = true;
        for (const filter of filters) {
            let value = '';
            if (filter.type === 'author.name') {
                value = map.author.name;
            } else {
                value = getNestedValue(map.snippet, filter.label);
            }

            switch (filter.comparison) {
                case 'contains':
                    valid = valid && Boolean(value && JSON.stringify(value).toLowerCase().includes(filter.value.toLowerCase()));
                    break;
                case 'notContains':
                    valid = valid && (!value || !value.includes(filter.value));
                    break;
                case 'isDefined':
                    valid = valid && value !== undefined;
                    break;
                case 'equals':
                    valid = valid && String(value) === String(filter.value);
                    break;
                default:
                    break;
            }
        }
        return valid;
    });
}

const currentUrl = new URL(window.location.href);

interface SavedSearch {
    name: string;
    property: string;
    filters: Array<Filter>;
    radioOption: string;
}

const SnippetSearchForm: FC<{totalMaps: Data[] }> = ({ totalMaps: mapsFromProps }) => {
    const [property, setProperty] = useState(currentUrl.searchParams.get('property') || '');
    const [filters, setFilters] = useState<Array<Filter>>(currentUrl.searchParams.get('filters')
        ? JSON.parse(currentUrl.searchParams.get('filters')!)
        : []);
    const [radioOption, setRadioOption] = useState(currentUrl.searchParams.get('radioOption') || '');
    const [results, setResults] = useState('');
    const [loading, setLoading] = useState(false);
    const [totalMaps, setTotalMaps] = useState(mapsFromProps);
    const [loadProgress, setLoadProgress] = useState(0);
    const [savedSearches, setSavedSearches] = useState<SavedSearch[]>(getSavedSearches());
    const [searchName, setSearchName] = useState('');
    const [showSaveDialog, setShowSaveDialog] = useState(false);

    const stringifiedResults = JSON.stringify(results, null, 2);
    const resultsLenth = Array.isArray(results) ? results.length : Object.keys(results).length

    useEffect(() => {
        currentUrl.searchParams.set('property', property);
        currentUrl.searchParams.set('filters', JSON.stringify(filters));
        currentUrl.searchParams.set('radioOption', radioOption);
        history.replaceState({}, '', currentUrl);

        performSearch();
    }, [property, filters, radioOption]);

    const performSearch = () => {
        const filteredMaps = filterMaps(totalMaps, filters);
        if (!radioOption) {
            return;
        }

        let results: any = null;
        switch (radioOption) {
            case 'list':
                results = listMapWithProperty(filteredMaps, property);
                break;
            case 'group-by-value':
                results = valueOfProperty(filteredMaps, property);
                break;
            case 'author':
                results = getAuthorFromProperty(filteredMaps);
                break;
            case 'value-with-map-id':
                results = getValueWithMapId(filteredMaps, property);
                break;
            default:
                console.error('Invalid option');
        }
        setResults(results);
    }

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        performSearch();
    };

    const handleCopyToClipboard = () => {
        const resultContent = document.getElementById('resultContent');
        if (resultContent) {
            const range = document.createRange();
            range.selectNode(resultContent);
            window.getSelection()?.removeAllRanges();
            window.getSelection()?.addRange(range);
            document.execCommand('copy');
            window.getSelection()?.removeAllRanges();
        }
    }

    const handleCopySearchToClipboard = () => {
        const urlToCopy = window.location.href;
        const dummyInput = document.createElement("textarea");
        document.body.appendChild(dummyInput);
        dummyInput.value = urlToCopy;
        dummyInput.select();
        document.execCommand("copy");
        document.body.removeChild(dummyInput);
    }

    const handleReloadMaps = () => {
        setLoading(true);
        initializeDB({
            forcePopulate: true,
            onProgress: (progress) => {
                setLoadProgress(progress);
            }
        })
        .then((results) => {
            if (results) {
                setLoadProgress(0);
                setTotalMaps(results);
            }
        })
        .finally(() => {
            setLoading(false);
        });
    }

    const handleSaveSearch = () => {
        if (!searchName.trim()) return;
        
        const search: SavedSearch = {
            name: searchName,
            property,
            filters,
            radioOption
        };
        
        saveSearch(search);
        setSavedSearches(getSavedSearches());
        setSearchName('');
        setShowSaveDialog(false);
    };

    const handleLoadSearch = (search: SavedSearch) => {
        setProperty(search.property);
        setFilters(search.filters);
        setRadioOption(search.radioOption);
    };

    const handleDeleteSearch = (index: number) => {
        deleteSavedSearch(index);
        setSavedSearches(getSavedSearches());
    };

    return (
        <StyledContainer>
            <Flex direction="column" gap="4">
                <Heading>Snippet recherche avancée</Heading>
                <div>Recherchez les maps et extrait de snippet avec des propriétés spécifiques</div>
                <Stack gap="3">
                    <div>
                        Nombre de maps: {totalMaps.length}
                        {loading ? (
                            <span style={{ marginLeft: 8 }}>Chargement... {(loadProgress * 100).toFixed()}% </span>
                        ) : null}
                    </div>
                    <div>
                        <Button disabled={loading} onClick={handleReloadMaps}>Recharger les maps</Button>
                    </div>
                </Stack>
                <Divider />
                {savedSearches.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <SubHeading>Recherches sauvegardées</SubHeading>
                        <Flex wrap="wrap" gap="2">
                            {savedSearches.map((search, index) => (
                                <Flex key={index} align="center" gap="1">
                                    <Button
                                        visual="outline"
                                        color="primary"
                                        onClick={() => handleLoadSearch(search)}
                                    >
                                        {search.name}
                                    </Button>
                                    <Button
                                        visual="icon"
                                        color="danger"
                                        size="iconSm"
                                        onClick={() => handleDeleteSearch(index)}
                                    >
                                        <LuX size={20} />
                                    </Button>
                                </Flex>
                            ))}
                        </Flex>
                    </div>
                )}
                <form onSubmit={handleSubmit}>
                    <Flex direction="column" gap="4">
                        <div>
                            <Label htmlFor="filter">Sélectionner un ou plusieurs filtres</Label>
                            <Stack gap="3">
                                {filters.map((filter, index) => (
                                    <Row key={index}>
                                        <Col>
                                            <Select
                                                value={filter.type}
                                                onChange={(val) => setFilters([...filters.slice(0, index), {...filter, type:val as any}, ...filters.slice(index + 1)])}
                                                options={FILTER_TYPE}
                                            />
                                        </Col>
                                        <Col>
                                            {filter.type === 'snippet.property' ? (
                                                <Input
                                                    type="text"
                                                    id="filter-value"
                                                    placeholder="Entrez la propriété"
                                                    value={filter.label}
                                                    onChange={(e) => setFilters([...filters.slice(0, index), {...filter, label: e.target.value as any}, ...filters.slice(index + 1)])}
                                                />
                                            ) : null}
                                        </Col>
                                        <Col>
                                            <Select
                                                value={filter.comparison}
                                                options={COMPARISON_OPTIONS}
                                                onChange={(val) => setFilters([...filters.slice(0, index), {...filter, comparison: val as any}, ...filters.slice(index + 1)])} />
                                        </Col>
                                        <Col>
                                            <Input
                                                disabled={filter.comparison === 'isDefined'}
                                                type="text"
                                                id="filter-value"
                                                placeholder="Entrez la valeur"
                                                value={filter.value}
                                                onChange={(e) => setFilters([...filters.slice(0, index), {...filter, value: e.target.value}, ...filters.slice(index + 1)])}
                                            />
                                        </Col>
                                        <Col>
                                            <Button
                                                visual="icon"
                                                size="iconSm"
                                                color="danger"
                                                onClick={() => setFilters([...filters.slice(0, index), ...filters.slice(index + 1)])}
                                                aria-label="Supprimer le filtre"
                                            >
                                                <LuX size={20} />
                                            </Button>
                                        </Col>
                                    </Row>
                                ))}
                                <div style={{ margin: '12px 0' }}>
                                    <Button
                                        onClick={() => setFilters([...filters, { type: 'snippet.property', label: '', value: '', comparison: 'contains'}])}
                                    >
                                        Ajouter un filtre
                                    </Button>
                                </div>
                            </Stack>
                        </div>
                        <div>
                            <Label htmlFor="property">
                                Nom de la propriété du snippet à extraire:
                                <span style={{ fontWeight: 'normal', fontSize: '12px', marginLeft: 4 }}>
                                    (Optionnel si vous ne souhaitez pas extraire de propriété)
                                </span>
                            </Label>
                            <Input
                                type="text"
                                id="property"
                                placeholder="Entrez une propriété"
                                value={property}
                                onChange={(e) => setProperty(e.target.value)}
                            />
                        </div>
                        <div>
                            <Label>Je veux:</Label>
                            <Flex direction="column" gap="2">
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Input
                                        style={{ width: 'auto' }}
                                        type="radio"
                                        name="radioOption"
                                        id="radioOption4"
                                        value="list"
                                        checked={radioOption === 'list'}
                                        onChange={(e) => setRadioOption(e.target.value)}
                                    />
                                    Lister toutes les maps id
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Input
                                        style={{ width: 'auto' }}
                                        type="radio"
                                        name="radioOption"
                                        id="radioOption1"
                                        value="author"
                                        checked={radioOption === 'author'}
                                        onChange={(e) => setRadioOption(e.target.value)}
                                    />
                                    Récupérer les maps groupés par auteur
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Input
                                        style={{ width: 'auto' }}
                                        disabled={!property}
                                        type="radio"
                                        name="radioOption"
                                        id="radioOption2"
                                        value="group-by-value"
                                        checked={radioOption === 'group-by-value'}
                                        onChange={(e) => setRadioOption(e.target.value)}
                                    />
                                    Regrouper les maps id par valeur de cette propriété
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Input
                                        style={{ width: 'auto' }}
                                        disabled={!property}
                                        type="radio"
                                        name="radioOption"
                                        id="radioOption3"
                                        value="value-with-map-id"
                                        checked={radioOption === 'value-with-map-id'}
                                        onChange={(e) => setRadioOption(e.target.value)}
                                    />
                                    Récupérer chaque valeur de cette propriété avec les maps id associés
                                </label>
                            </Flex>
                        </div>
                    </Flex>
                </form>
                <Stack id="result" gap="3" style={{ marginTop: 32 }}>
                    <h2>Résultat:</h2>
                    {resultsLenth ? (
                        <Flex gap="2" align="center" wrap="wrap">
                            <Button onClick={handleCopyToClipboard}>
                                Copier les résultats
                            </Button>
                            <Button onClick={handleCopySearchToClipboard}>
                                Copier l'URL de recherche
                            </Button>
                            <Button onClick={() => setShowSaveDialog(true)}>
                                Sauvegarder la recherche
                            </Button>
                            <div style={{ margin: '8px 0' }}>
                                {resultsLenth} résultats
                            </div>
                        </Flex>
                    ) : null}
                    <Pre><code id="resultContent">{stringifiedResults}</code></Pre>
                </Stack>
            </Flex>
            {showSaveDialog && (
                <ModalOverlay>
                    <ModalContent>
                        <ModalHeader>
                            <div>Sauvegarder la recherche</div>
                            <Button
                                type="button"
                                visual="icon"
                                size="iconSm"
                                onClick={() => setShowSaveDialog(false)}
                                aria-label="Fermer"
                            >
                                <LuX size={20} />
                            </Button>
                        </ModalHeader>
                        <div style={{ marginBottom: 16 }}>
                            <Label>Nom de la recherche</Label>
                            <Input
                                type="text"
                                value={searchName}
                                onChange={(e) => setSearchName(e.target.value)}
                                placeholder="Entrez un nom pour cette recherche"
                            />
                        </div>
                        <ModalFooter>
                            <Button visual="solid" onClick={() => setShowSaveDialog(false)}>
                                Annuler
                            </Button>
                            <Button onClick={handleSaveSearch}>
                                Sauvegarder
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </ModalOverlay>
            )}
        </StyledContainer>
    );
};

const appContainer = document.getElementById('app')!;

initializeDB({
    onProgress: (progress) => {
        appContainer.innerHTML = `Chargement... ${(progress * 100).toFixed()}%`;
    }
}).then(async () => {
    const totalMaps = await getLivemapsFromDB();
    createRoot(appContainer).render(<SnippetSearchForm totalMaps={totalMaps} />);
});
